import { FormGroup, Input, Progress, Radio, RadioItem, Spinner } from '@agro-club/agroclub-shared'
import { useAFormHandler } from 'analytics/hooks'
import { useFormik } from 'formik'
import { refetchFunc, useSingleEntity } from 'modules/domain/common/hooks'
import {
  UsContractDTO,
  UsContractTypes,
  UsDeliveryTypes,
  UsPricingTypes,
  UsTransportModes,
} from 'modules/domain/usContracts/types'
import { endpoints } from 'modules/endpoints'
import { apiClient } from 'modules/utils/httpClient'
import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import FormSelectShared from 'views/components/FormSelectShared'
import { ResponsiveModal } from 'views/components/ResponsiveModal/ResponsiveModal'
import { SharedModalContent, SharedModalInputs } from 'views/components/SharedModal'
import { Controls, PricingTypeRadio } from './CommonFields'
import { FormCompany } from 'views/components/form/FormCompany'
import { USCompany } from 'modules/domain/company/types'
import styled from 'styled-components'
import { FormDatePicker } from 'views/components/form/FormDatePicker'
import { FormEnumSelect } from 'views/components/form/EnumSelect/FormEnumSelect'
import { PositionFields } from './PositionFields'
import { ShortFormControls } from 'views/styled/common'
import { ProductFuturesSymbol } from 'modules/domain/collection/types'

type Props = {
  refetch: refetchFunc
  onClose: () => void
}

export const MarkContainer = styled.div`
  position: absolute;
  top: 50%;
  right: 0;
  transform: translateY(-50%);
`

export const UsContractAdd: FC<Props> = ({ onClose, refetch }) => {
  const { t } = useTranslation('usContracts')
  const { formProgress, formHandler } = useAFormHandler()

  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null)
  const [companyProgress, company] = useSingleEntity<USCompany>(
    selectedCompanyId ? endpoints.company(selectedCompanyId) : undefined,
  )

  const formik = useFormik<UsContractDTO>({
    initialValues: {
      contract_type: UsContractTypes.sale,
      sales_person: null,
      company: null,
      market_zone: null,
      contract_date: null,
      product: null,
      delivery_type: UsDeliveryTypes.fob,
      pricing_type: UsPricingTypes.priced,
      positions: [
        {
          symbol: '',
          symbolObjectId: null,
          pricing_type: UsPricingTypes.priced,
          quantity: '',
          futures_price: '',
          basis: '',
          price: '',
          freight_cost: '',
          start_date: '',
          end_date: '',
          transport_mode: UsTransportModes.truck,
        },
      ],
    },
    onSubmit: formHandler(
      async (values) => {
        // hack because symbol on backend simple string for now
        values.positions.forEach((position) => {
          position.symbol = productSymbols?.find((p) => p.id == position.symbolObjectId)?.symbol ?? ''
        })
        return await apiClient.post(endpoints.usContracts(), values)
      },
      {
        onSuccess: () => {
          refetch?.()
          onClose()
        },
        onError: (error) => {
          const { errors } = error as any
          errors?.positions?.forEach((p) => (p.symbolObjectId = p.symbol))
          formik.setErrors(errors || {})
        },
      },
    ),
  })

  const [, productSymbols] = useSingleEntity<ProductFuturesSymbol[]>(
    formik.values.product ? `${endpoints.futuresSymbols()}?product_id=${formik.values.product}` : undefined,
  )

  const onChangeCompany = (value: string) => {
    formik.setFieldValue('company', value)
    setSelectedCompanyId(value)
  }
  let addressString = ''
  if (company?.main_address) {
    addressString = company.main_address.short_address
  } else if (company) {
    addressString = t('noAddress')
  }

  return (
    <ResponsiveModal
      title={t('addModal.title')}
      id="addUsContractModal"
      customFooter={<Controls formik={formik} formProgress={formProgress} onClose={onClose} />}
      progress={formProgress}
      onClose={onClose}
      //headerHeight={89}
      size="medium"
    >
      <SharedModalContent>
        <SharedModalInputs>
          <FormGroup
            error={formik.errors.contract_type}
            label={t('fields.type')}
            // TODO center
            style={{ alignItems: 'center' }}
          >
            <Radio
              onChange={(value) => formik.setFieldValue('contract_type', value)}
              value={formik.values.contract_type}
            >
              <RadioItem value={UsContractTypes.sale} label={t('contractTypes.sale')} />
              <RadioItem value={UsContractTypes.purchase} label={t('contractTypes.purchase')} />
            </Radio>
          </FormGroup>
          <FormSelectShared
            label={t('fields.salesPerson')}
            formik={formik}
            fieldName="sales_person"
            placeholder=""
            getLabel={(label) => label.full_name}
            endpoint={endpoints.coordinatorsShort()}
            searchField="search"
          />
          <FormCompany formik={formik} onChange={onChangeCompany} allowClear={false} />
          <FormGroup
            label={t('fields.address')}
            afterControlComponent={
              companyProgress == Progress.WORK && (
                <MarkContainer>
                  <Spinner size="small" />
                </MarkContainer>
              )
            }
          >
            <Input value={addressString} disabled />
          </FormGroup>
          <FormDatePicker
            formik={formik}
            label={t('fields.contractDate')}
            fieldName="contract_date"
            id="contract_date"
          />
          <FormSelectShared
            label={t('fields.product')}
            formik={formik}
            endpoint={endpoints.products()}
            simpleApiFormat
            getLabel={(dto) => dto.title}
            onChange={(value) => {
              formik.setFieldValue('product', value)
            }}
            fieldName="product"
            showSearch={false}
          />
          <ShortFormControls>
            <FormEnumSelect
              label={t('fields.delivery')}
              formik={formik}
              fieldName="delivery_type"
              translationNameSpace="usContracts:deliveryTypes"
              enumType={UsDeliveryTypes}
            />
          </ShortFormControls>
          <PricingTypeRadio formik={formik} fieldName="pricing_type" />
          <PositionFields formik={formik} />
        </SharedModalInputs>
      </SharedModalContent>
    </ResponsiveModal>
  )
}
